import React from 'react';
import ReactSVG from 'react-svg';
import { FaFileAlt, FaFilm, FaImage, FaVolumeUp } from 'react-icons/fa';
import { IconBaseProps } from 'react-icons';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import * as actions from '~/store/actions';
import { ChannelTypeEnum, MessageTypeEnum } from '~/enums/conversations';
import { Conversation, Message } from '~/interfaces/conversations';
import { useNavigation } from '~/hooks';
import { dayjs } from '~/dependencies';
import { log, isUndefined } from '~/helpers';
import { UserIcon } from '~/assets/svgIcons';
import {
  getChannelIconById,
  getAttachmentErrorMessage,
} from '~/helpers/conversation';
import RoundedCheckbox from '../RoundedCheckbox';
import AssignedAgentSelector from '../AssignedAgentSelector';
import TTRExpiredBadge from '../TTRExpiredBadge';
import SLABreachedBadge from '../SLABreachedBadge';
import TTRCounterBadge from '../TTRCounterBadge';
import {
  ConversationItem,
  Details,
  Avatar,
  Headerline,
  Caption,
  ContactName,
  RightSide,
  Counter,
  MessagePreview,
  Badge,
  ContactMessagePreviewContainer,
} from './styles';

type AvatarContactChannelProps = {
  contactName: string;
  channelType: ChannelTypeEnum;
};

type LegacyCompatibleIcon = (props: IconBaseProps) => React.ReactElement;

function LastMessageTime({ dataTime }: { dataTime: string }) {
  return (
    <span
      data-test-id="message-data-time"
      style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
      {dataTime}
    </span>
  );
}

function LastMessagePreview({ message }: { message: string }) {
  return (
    <MessagePreview data-test-id="message-caption">{message}</MessagePreview>
  );
}

function ContactMessagePreview() {
  return (
    <ContactMessagePreviewContainer>
      <ReactSVG src={UserIcon} className="icon-contact" />
      Shared Contact
    </ContactMessagePreviewContainer>
  );
}

type AttachmentPreviewType = 'image' | 'video' | 'audio' | 'file';

function getAttachmentPreviewIcon(
  type: AttachmentPreviewType
): LegacyCompatibleIcon {
  if (type === 'image') {
    return FaImage as LegacyCompatibleIcon;
  }

  if (type === 'video') {
    return FaFilm as LegacyCompatibleIcon;
  }

  if (type === 'audio') {
    return FaVolumeUp as LegacyCompatibleIcon;
  }

  return FaFileAlt as LegacyCompatibleIcon;
}

function getAttachmentPreviewLabel(type: AttachmentPreviewType) {
  if (type === 'image') {
    return 'Image';
  }

  if (type === 'video') {
    return 'Video';
  }

  if (type === 'audio') {
    return 'Audio';
  }

  return 'File';
}

function getAttachmentType(mediaContentType: string | undefined) {
  if (mediaContentType?.startsWith('image/')) {
    return 'image';
  }

  if (mediaContentType?.startsWith('video/')) {
    return 'video';
  }

  if (mediaContentType?.startsWith('audio/')) {
    return 'audio';
  }

  return 'file';
}

function FileMessagePreview({ type }: { type: AttachmentPreviewType }) {
  const PreviewIcon = getAttachmentPreviewIcon(type);
  const label = getAttachmentPreviewLabel(type);

  return (
    <MessagePreview data-test-id="message-caption-file">
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <PreviewIcon />
        {label}
      </span>
    </MessagePreview>
  );
}

function renderLastMessageContent(messageInfo: Message | undefined) {
  if (!messageInfo) return '...';

  const { message, messageType, metadata } = messageInfo;

  const hasAttachmentMediaError = Boolean(metadata?.mediaError?.code);

  if (messageType === MessageTypeEnum.TEXT) {
    if (hasAttachmentMediaError) {
      return (
        <LastMessagePreview
          message={getAttachmentErrorMessage(metadata?.mediaError)}
        />
      );
    }

    return <LastMessagePreview message={message || '...'} />;
  }

  if (messageType === MessageTypeEnum.LOCATION) {
    return <LastMessagePreview message="📍 Location" />;
  }

  if (messageType === MessageTypeEnum.CONTACT) {
    return <ContactMessagePreview />;
  }

  if (
    messageType === MessageTypeEnum.IMAGE ||
    messageType === MessageTypeEnum.STICKER ||
    messageType === MessageTypeEnum.VIDEO ||
    messageType === MessageTypeEnum.AUDIO ||
    messageType === MessageTypeEnum.FILE
  ) {
    const media = metadata?.media;
    const mimeTypeOrContentType = Array.isArray(media)
      ? media[0]?.mimeType ?? media[0]?.contentType
      : media?.mimeType;

    const attachmentType = getAttachmentType(mimeTypeOrContentType);

    return <FileMessagePreview type={attachmentType} />;
  }

  return '...';
}

function UnreadMessageCount({ count }: { count: number }) {
  return (
    <Counter
      data-test-id="message-count"
      overlap="circular"
      color="primary"
      badgeContent={count}
    />
  );
}

function AvatarContactChannel({
  contactName,
  channelType,
}: AvatarContactChannelProps) {
  const initials = contactName
    ?.trim()
    .split(' ')
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');

  return (
    <Badge
      data-test-id={`channel-badge-${channelType}`}
      overlap="circular"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      badgeContent={<ReactSVG src={getChannelIconById(channelType)} />}>
      <Avatar>{initials}</Avatar>
    </Badge>
  );
}

function formatConversationDate(date: string, userTz: string) {
  const isSameDay = dayjs()
    .tz(userTz)
    .isSame(date, 'day');

  return dayjs(date)
    .tz(userTz)
    .format(isSameDay ? 'HH:mm' : 'MMM D');
}

type RowProps = {
  index: number;
  item: Conversation;
};

// @ts-ignore
export default function Row({ item, index }: RowProps) {
  // HOOKS
  const dispatch = useDispatch();
  const navigate = useNavigation();

  // SELECTORS
  const openSelection = useSelector(
    (state: RootStateOrAny) => state.conversation.openSelection
  );
  const userTz = useSelector(
    (state: RootStateOrAny) => state.auth.userTimeZone
  );

  // CONST's
  const {
    id,
    assignedName,
    ttr,
    unread,
    messages,
    selected,
    contactName,
    contactNumber,
    expiresAt,
  } = item;

  const lastMessage = messages[messages.length - 1];
  const lastMessageChannelType = (lastMessage as any)?.channelType;
  const creationDate = formatConversationDate(lastMessage?.createdAt, userTz);

  const isExpired = ttr !== null && ttr <= 0;

  const isWhatsAppChannel = lastMessageChannelType === ChannelTypeEnum.WHATSAPP;
  /**
   * WhatsApp: isTtrSlaBreached only when within 24h of the last inbound message (original behavior)
   * Non-WhatsApp (e.g., SMS): isTtrSlaBreached is always true when expired with an inbound last message, regardless of time elapsed
   */
  const isTtrSlaBreached =
    isExpired &&
    lastMessage?.direction === 1 &&
    (isWhatsAppChannel
      ? dayjs().diff(dayjs(lastMessage?.createdAt), 'hour', true) < 24
      : true);

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    item.selected = event.target.checked;
    dispatch(actions.updateConversation(item));
  };

  const handleClickConversation = () => {
    log.info('Clicked conversation: ', item, {
      isExpired,
      isWhatsAppChannel,
      isTtrSlaBreached,
      lastMessageChannelType,
    });
    dispatch(actions.setConversationLastReadIndex(index));
    navigate.chatView(item);
  };

  return (
    <ConversationItem
      key={id}
      id={id}
      data-test-id="conversation-badge"
      isUnread={unread > 0}>
      {openSelection ? (
        <RoundedCheckbox checked={selected} onChange={handleSelection} />
      ) : null}
      <AvatarContactChannel
        contactName={contactName}
        channelType={lastMessageChannelType}
      />
      <Details onClick={handleClickConversation}>
        <Headerline>
          <RightSide>
            {!isExpired && expiresAt && !isUndefined(ttr) ? (
              <TTRCounterBadge expireIn={ttr} />
            ) : null}
            {isTtrSlaBreached && expiresAt ? <SLABreachedBadge /> : null}
            {isExpired && !isTtrSlaBreached && expiresAt !== null ? (
              <TTRExpiredBadge />
            ) : null}

            <ContactName data-test-id="contact-name" title={contactNumber}>
              {contactName}
            </ContactName>
            <AssignedAgentSelector
              conversationId={id}
              agentName={assignedName}
            />
          </RightSide>
          <LastMessageTime dataTime={creationDate} />
        </Headerline>
        <Caption data-test-id="message-caption">
          {renderLastMessageContent(lastMessage)}
          {unread > 0 ? <UnreadMessageCount count={unread} /> : null}
        </Caption>
      </Details>
    </ConversationItem>
  );
}
